import { Link } from "react-router-dom";
import { Instagram, ShoppingBag, Mail, Phone, MapPin } from "lucide-react";

const COMPANY_EMAIL = "laura@itisobvious.co.uk";
const COMPANY_PHONE = "07979 119771";

const ContactPage = () => {
  return (
    <main className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-20 bg-[var(--obvious-purple)] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <span className="font-['Space_Mono'] text-lg bg-[var(--obvious-yellow)] text-black px-4 py-2 border-4 border-[var(--obvious-black)] inline-block mb-6">
              GET IN TOUCH
            </span>
            <h1 className="font-['Anton'] text-6xl md:text-8xl text-[var(--obvious-white)] leading-[0.9] mb-6">
              LET'S MAKE
              <br />
              <span className="text-[var(--obvious-teal)]">SOMETHING</span>
              <br />
              OBVIOUS
            </h1>
          </div>
        </div>

        {/* Decorative */}
        <div className="absolute top-10 left-10 w-32 h-32 border-8 border-[var(--obvious-teal)] rounded-full opacity-40 spin-slow" />
        <div className="absolute bottom-10 right-10 w-24 h-24 bg-[var(--obvious-coral)] rotate-45 opacity-30 floating" />
        <div className="absolute top-20 right-20 w-16 h-16 bg-[var(--obvious-yellow)] opacity-40 floating" style={{ animationDelay: '1s' }} />
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-[var(--obvious-yellow)] relative" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Intro Copy */}
            <div data-testid="contact-intro">
              <h2 className="font-['Anton'] text-4xl md:text-5xl text-[var(--obvious-black)] mb-6">
                SAY HELLO.
                <br />
                <span className="text-[var(--obvious-purple)]">PROPERLY.</span>
              </h2>

              <div className="card-90s p-8 bg-[var(--obvious-white)] -rotate-1 hover:rotate-0 transition-transform space-y-5 font-['Space_Mono'] text-base md:text-lg text-gray-800 leading-relaxed">
                <p>
                  We ditched the contact form. Why? Robots kept filling it in and, frankly, your big idea deserves better than a dropdown menu.
                </p>
                <p>
                  Commissioning a <span className="font-bold">one-off piece</span>? Chasing a <span className="font-bold">custom sign</span>? Want to collaborate with the collective on something loud, weird or wonderful? <span className="bg-[var(--obvious-yellow)] px-2 py-1 border-2 border-[var(--obvious-black)] font-bold">Send it our way.</span>
                </p>
                <p>
                  Drop us an <span className="text-[var(--obvious-coral)] font-bold">email</span>, ring the <span className="text-[var(--obvious-coral)] font-bold">phone</span>, or slide into our <span className="text-[var(--obvious-coral)] font-bold">Instagram DMs</span>. We'll reply like actual humans, because we are ones.
                </p>
                <p className="font-['Anton'] text-2xl text-[var(--obvious-black)] pt-2">
                  NO FORMS. NO FUSS. JUST THE COLLECTIVE.
                </p>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-['Anton'] text-4xl md:text-5xl text-[var(--obvious-black)] mb-8">
                  PICK YOUR POISON
                </h2>
              </div>

              {/* Contact Cards */}
              <div className="space-y-6">
                <a
                  href={`mailto:${COMPANY_EMAIL}`}
                  className="card-90s p-6 flex items-center gap-6 hover:bg-[var(--obvious-teal)] transition-colors group"
                  data-testid="contact-email-card"
                >
                  <div className="w-16 h-16 bg-[var(--obvious-coral)] border-4 border-[var(--obvious-black)] flex items-center justify-center group-hover:bg-[var(--obvious-black)]">
                    <Mail size={32} strokeWidth={3} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-['Anton'] text-xl">EMAIL US</h3>
                    <p className="font-['Space_Mono'] text-gray-600 group-hover:text-black break-all">
                      {COMPANY_EMAIL}
                    </p>
                  </div>
                </a>

                <a
                  href={`tel:${COMPANY_PHONE.replace(/\s/g, '')}`}
                  className="card-90s p-6 flex items-center gap-6 hover:bg-[var(--obvious-yellow)] transition-colors group"
                  data-testid="contact-phone-card"
                >
                  <div className="w-16 h-16 bg-[var(--obvious-teal)] border-4 border-[var(--obvious-black)] flex items-center justify-center group-hover:bg-[var(--obvious-black)]">
                    <Phone size={32} strokeWidth={3} className="text-black group-hover:text-white" />
                  </div>
                  <div>
                    <h3 className="font-['Anton'] text-xl">CALL US</h3>
                    <p className="font-['Space_Mono'] text-gray-600 group-hover:text-black">
                      {COMPANY_PHONE}
                    </p>
                  </div>
                </a>

                <a
                  href="https://www.instagram.com/obvious_signs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card-90s p-6 flex items-center gap-6 hover:bg-[var(--obvious-coral)] hover:text-white transition-colors group"
                  data-testid="contact-instagram-card"
                >
                  <div className="w-16 h-16 bg-[var(--obvious-purple)] border-4 border-[var(--obvious-black)] flex items-center justify-center group-hover:bg-[var(--obvious-black)]">
                    <Instagram size={32} strokeWidth={3} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-['Anton'] text-xl">INSTAGRAM</h3>
                    <p className="font-['Space_Mono'] text-gray-600 group-hover:text-white/80">
                      @obvious_signs
                    </p>
                  </div>
                </a>

                <Link
                  to="/shop"
                  className="card-90s p-6 flex items-center gap-6 hover:bg-[var(--obvious-black)] hover:text-white transition-colors group"
                  data-testid="contact-etsy-card"
                >
                  <div className="w-16 h-16 bg-[var(--obvious-yellow)] border-4 border-[var(--obvious-black)] flex items-center justify-center group-hover:bg-[var(--obvious-teal)]">
                    <ShoppingBag size={32} strokeWidth={3} className="text-black" />
                  </div>
                  <div>
                    <h3 className="font-['Anton'] text-xl">SHOP THE COLLECTIVE</h3>
                    <p className="font-['Space_Mono'] text-gray-600 group-hover:text-white/80">
                      Prints, one-offs &amp; ready-made pieces
                    </p>
                  </div>
                </Link>
              </div>

              {/* FAQ Card */}
              <div className="card-90s p-8 bg-[var(--obvious-black)] text-white rotate-1 hover:rotate-0 transition-transform">
                <h3 className="font-['Anton'] text-2xl text-[var(--obvious-teal)] mb-4">
                  QUICK ANSWERS
                </h3>
                <div className="space-y-4 font-['Space_Mono'] text-sm">
                  <div>
                    <p className="text-[var(--obvious-coral)] font-bold">How long does a project take?</p>
                    <p className="text-gray-400">Typically 1-3 weeks. Good things take time. Rushed things look rushed.</p>
                  </div>
                  <div>
                    <p className="text-[var(--obvious-coral)] font-bold">Do you ship?</p>
                    <p className="text-gray-400">UK-wide delivery. Your sign, wrapped like a present. Because it is one.</p>
                  </div>
                  <div>
                    <p className="text-[var(--obvious-coral)] font-bold">Can I see designs before production?</p>
                    <p className="text-gray-400">Obviously. We're not monsters. Full mockups for approval before we cut anything.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative */}
        <div className="absolute top-10 right-10 w-24 h-24 border-8 border-[var(--obvious-black)] rounded-full opacity-20" />
        <div className="absolute bottom-20 left-10 w-16 h-16 bg-[var(--obvious-coral)] rotate-45 opacity-30" />
      </section>

      {/* Location Section with Brand Graphic */}
      <section className="py-20 bg-[var(--obvious-black)]" data-testid="location-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Brand Graphic */}
            <div className="flex justify-center order-2 md:order-1">
              <div className="card-90s p-4 bg-[var(--obvious-white)] -rotate-2 hover:rotate-0 transition-transform">
                <img
                  src="https://customer-assets.emergentagent.com/job_funky-nineties/artifacts/coe6mtrk_ChatGPT%20Image%20Feb%205%2C%202026%2C%2010_40_23%20PM.png"
                  alt="Visible As F*ck"
                  className="w-full max-w-sm"
                />
              </div>
            </div>

            {/* Location Info */}
            <div className="text-center md:text-left order-1 md:order-2">
              <div className="inline-flex items-center gap-4 p-6 border-4 border-[var(--obvious-teal)] mb-6">
                <MapPin size={48} strokeWidth={3} className="text-[var(--obvious-teal)]" />
                <div className="text-left">
                  <h3 className="font-['Anton'] text-2xl text-[var(--obvious-white)]">
                    BASED IN THE UK
                  </h3>
                  <p className="font-['Space_Mono'] text-gray-400">
                    Delivering & installing nationwide
                  </p>
                </div>
              </div>
              <p className="font-['Space_Mono'] text-lg text-gray-400 max-w-md">
                From Scotland to Cornwall – if you need a sign, we'll get it to you.
                Design, build, deliver, install. <span className="text-[var(--obvious-coral)]">The full service.</span>
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
